import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Ship, Globe2, Handshake, Award, BadgeDollarSign, Star } from "lucide-react";
import { COMPANY } from "../data/site";

const ABOUT_IMG = "https://images.pexels.com/photos/4481328/pexels-photo-4481328.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1400";
const CARGO = "https://images.pexels.com/photos/8555366/pexels-photo-8555366.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1400";

const values = [
    { icon: BadgeDollarSign, title: "Competitive Wholesale Prices", text: "We source efficiently so our partners enjoy strong margins on every case." },
    { icon: Ship, title: "Reliable Import & Export Solutions", text: "End-to-end logistics that move premium goods from origin to market dependably." },
    { icon: Handshake, title: "Strong Customer Service", text: "Responsive, relationship-driven support for every retailer and partner." },
    { icon: Star, title: "Trusted Business Partner", text: "A reputation built on consistency, transparency, and on-time delivery." },
    { icon: Award, title: "Premium & Exclusive Brands", text: "Carefully curated, exclusive distribution of quality international brands." },
    { icon: Globe2, title: "Quality Products", text: "Halal and FDA-aligned products that meet high standards across categories." },
];

export default function About() {
    return (
        <div data-testid="about-page">
            <section className="relative bg-foreground text-background">
                <div className="absolute inset-0 opacity-30">
                    <img src={ABOUT_IMG} alt="Warehouse distribution" className="w-full h-full object-cover" />
                </div>
                <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-28">
                    <p className="text-sm uppercase tracking-[0.3em] text-secondary mb-4">About YATTO LLC</p>
                    <h1 className="font-heading font-black text-5xl sm:text-6xl tracking-tight max-w-3xl leading-[0.95]">A trusted name in wholesale distribution</h1>
                    <p className="text-background/75 max-w-2xl mt-6 text-lg">{COMPANY.tagline}</p>
                </div>
            </section>

            <section className="py-24 sm:py-32">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-16 items-center">
                    <div>
                        <h2 className="font-heading font-bold text-4xl tracking-tight mb-6">Connecting quality brands with the right markets</h2>
                        <div className="space-y-5 text-muted-foreground leading-relaxed">
                            <p>YATTO LLC is a trusted company specializing in the import, export, and wholesale distribution of premium-quality products. We connect quality brands with the right markets through trusted sourcing, competitive wholesale pricing, and excellent customer service.</p>
                            <p>We supply wholesalers, retailers, grocery stores, gas stations, restaurants, and business partners with reliable products and strong value. Our portfolio spans olive oil, Palestinian food products, spices and pantry items, beverages, snacks, energy drinks, and international groceries.</p>
                            <p>From sourcing to delivery, our mission is simple: deliver premium products, dependable service, and pricing that helps our partners grow.</p>
                        </div>
                        <div className="mt-8 space-y-3">
                            {["Exclusive distribution of premium brands", "Competitive wholesale pricing", "Reliable import & export solutions", "Quality products you can trust"].map((t) => (
                                <div key={t} className="flex items-center gap-3 text-foreground">
                                    <CheckCircle2 size={18} className="text-primary shrink-0" /> <span className="text-sm font-medium">{t}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="relative">
                        <img src="/assets/about/about-us.jpg" alt="YATTO LLC wholesale distribution warehouse stocked with food, beverages and grocery products" className="w-full aspect-[4/5] object-cover" />
                    </div>
                </div>
            </section>

            <section className="py-24 sm:py-32 bg-muted">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <p className="text-sm uppercase tracking-[0.2em] text-secondary mb-3">What Sets Us Apart</p>
                    <h2 className="font-heading font-bold text-4xl sm:text-5xl tracking-tight mb-14">Our commitment to partners</h2>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-border border border-border">
                        {values.map((v) => (
                            <div key={v.title} className="bg-background p-8">
                                <v.icon size={30} className="text-primary mb-5" />
                                <h3 className="font-heading font-bold text-lg mb-2">{v.title}</h3>
                                <p className="text-sm text-muted-foreground leading-relaxed">{v.text}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="bg-primary text-primary-foreground">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20 flex flex-col sm:flex-row items-center justify-between gap-8">
                    <div>
                        <h2 className="font-heading font-bold text-4xl tracking-tight mb-2">Become a YATTO partner</h2>
                        <p className="text-primary-foreground/70">Request a wholesale account and start stocking premium brands.</p>
                    </div>
                    <div className="flex gap-4">
                        <Link to="/catalog" className="bg-secondary text-secondary-foreground px-7 py-4 font-semibold hover:-translate-y-1 transition-transform flex items-center gap-2">View Catalog <ArrowRight size={18} /></Link>
                        <Link to="/contact" className="border border-primary-foreground/30 px-7 py-4 font-semibold hover:bg-background hover:text-foreground transition-colors">Contact Us</Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
